import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSuccessionTheme } from '../../SuccessionThemeProvider';
import AvatarDisplay from '../../../AvatarDisplay/AvatarDisplay';
import { getRecognitionRibbonColor } from '../../../../lib/recognitionLevelColor';

const ProfileHeaderSection = ({ user, company, onEdit, onAvatarEdit, companySettings }) => {
  const intl = useIntl();
  const { theme } = useSuccessionTheme();

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* Avatar */}
          <div className="relative group">
            <AvatarDisplay
              src={user.profile_picture}
              alt={user.name}
              size="xl"
              companySettings={companySettings}
              initialsName={user.name}
            />
            <button
              onClick={onAvatarEdit}
              className="absolute inset-0 w-24 h-24 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            {company?.feature_types?.includes('profile_recognition_level') && user.recognition_level && (
              <div className="absolute -bottom-2 left-0 right-0 flex justify-center z-10">
                <span
                  className="inline-block px-1.5 py-0.5 rounded text-white font-bold uppercase tracking-wide text-center leading-tight shadow-sm"
                  style={{
                    maxWidth: '5.5rem',
                    fontSize: user.recognition_level.length > 10 ? '7px' : '9px',
                    backgroundColor: getRecognitionRibbonColor(user.recognition_level, company),
                  }}
                >
                  {user.recognition_level}
                </span>
              </div>
            )}
          </div>

          {/* Name, Headline, Location */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-succession-text-primary mb-1">
              {user.name || intl.formatMessage({ id: 'succession.profile_edit.sections.profile_header.add_your_name' })}
            </h2>
            <p className="text-lg text-succession-text-secondary mb-2">
              {user.headline || intl.formatMessage({ id: 'succession.profile_edit.sections.profile_header.add_a_professional_headline' })}
            </p>
            {user.location && (
              <div className="flex items-center text-succession-text-muted">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm">{user.location}</span>
              </div>
            )}
            {user.motivation === 'growth' && (
              <div className="flex items-center mt-2 text-sm">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {user.show_real_name ? (
                  <span className="text-succession-primary">{intl.formatMessage({ id: 'succession.profile_edit.sections.profile_header.displaying_as_first_name' })}</span>
                ) : (
                  <span className="text-succession-text-muted">{intl.formatMessage({ id: 'succession.profile_edit.sections.profile_header.anonymous_profile' })}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Edit Button */}
        <button
          onClick={onEdit}
          data-tutorial-id="profile-edit-button"
          className="inline-flex items-center px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
        </button>
      </div>
    </div>
  );
};

ProfileHeaderSection.propTypes = {
  user: PropTypes.object.isRequired,
  company: PropTypes.shape({
    feature_types: PropTypes.arrayOf(PropTypes.string),
  }),
  onEdit: PropTypes.func.isRequired,
  onAvatarEdit: PropTypes.func.isRequired,
  companySettings: PropTypes.shape({
    new_avatars_enabled: PropTypes.bool,
    avatar_directory: PropTypes.string,
    avatar_background_color: PropTypes.string,
    primary_color: PropTypes.string,
  }),
};

export default ProfileHeaderSection;
